import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AntiMemLeak } from 'src/app/core/form-utils/anti-mem-leak/anti-mem-leak';
import { ProviderService } from 'src/app/core/provider.service';
import { Observable } from 'rxjs';
import { RegionModel } from './models/region-model';
import { CountryModel } from './models/country-model';
import { TreeNode } from 'primeng/api';
import { TreeSelectModule } from 'primeng/treeselect';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TreeTableModule } from 'primeng/treetable';

@Component({
  selector: 'app-region-country-filter',
  templateUrl: './region-country-filter.component.html',
  styleUrls: ['./region-country-filter.component.scss'],
  standalone: true,
  imports: [
    TreeSelectModule,
    ReactiveFormsModule,
    TranslateModule,
    CommonModule,
    FormsModule,
    CheckboxModule,
    ButtonModule,
    TreeTableModule,
  ],
})
export class RegionCountryFilterComponent extends AntiMemLeak implements OnInit, AfterViewInit {
  @Input()
  resetFilterObservable?: Observable<any>;
  @Input({ required: true })
  formGroupInput!: FormGroup;
  @Input()
  formControlNameInput = 'countries';
  @Input()
  label?: string;
  @Input()
  showAllClassesToggle = false;

  treeNodes: TreeNode[] = [];
  selectedNodes: TreeNode[] | null = null;
  loading = true;
  allClasses = false;

  get isFormControlReady(): boolean {
    return !!this.formGroupInput?.get(this.formControlNameInput);
  }

  constructor(private providerService: ProviderService) {
    super();
  }

  ngOnInit(): void {
    this.getRegionCountryTree();
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.resetFilterObservable?.subscribe(() => {
        this.selectedNodes = null;
        this.formGroupInput.get(this.formControlNameInput)?.setValue([]);
      })
    );

    const currentValue = this.formGroupInput.get(this.formControlNameInput)?.value;
    if (currentValue && Array.isArray(currentValue) && currentValue.length > 0) {
      this.selectedNodes = this.convertCountriesToTreeNodes(currentValue);
    }
  }

  clearFilter(): void {
    this.selectedNodes = null;
    this.formGroupInput.get(this.formControlNameInput)?.setValue([], { emitEvent: true });
  }

  async getRegionCountryTree(): Promise<void> {
    this.loading = true;
    try {
      const regionsAndCountries = await this.providerService.regionCountryFilterNewService.getItuRegionsAndCountries(
        this.allClasses
      );

      this.buildTreeNodes(regionsAndCountries);
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  async onAllClassesChange(): Promise<void> {
    await this.getRegionCountryTree();
    const currentValue = this.formGroupInput.get(this.formControlNameInput)?.value;
    this.selectedNodes =
      currentValue && Array.isArray(currentValue) && currentValue.length > 0
        ? this.convertCountriesToTreeNodes(currentValue)
        : null;
    this.onSelectionChange(this.selectedNodes);
  }

  get totalCountriesCount(): number {
    return this.treeNodes.reduce((count, node) => count + (node.children?.length ?? 0), 0);
  }

  get allCountriesSelected(): boolean {
    return (
      this.totalCountriesCount > 0 &&
      this.convertTreeNodesToCountries(this.selectedNodes).length === this.totalCountriesCount
    );
  }

  toggleSelectAll(): void {
    this.selectedNodes = this.allCountriesSelected ? null : this.treeNodes.flatMap((node) => node.children ?? []);
    this.onSelectionChange(this.selectedNodes);
  }

  buildTreeNodes(regionsAndCountries: { region: RegionModel; countries: CountryModel[] }[]): void {
    this.treeNodes = regionsAndCountries.map(({ region, countries }) => ({
      label: `${region.regionCode} - ${region.regionName}`,
      data: region,
      key: `region-${region.regionID}`,
      selectable: true,
      leaf: false,
      children: countries.map((country: CountryModel) => ({
        label: `${country.isoCode} - ${country.shortName}`,
        data: country,
        key: `country-${country.countryID}`,
        selectable: true,
        leaf: true,
      })),
    }));
  }

  findNodeByKey(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) {
        return node;
      }
      if (node.children) {
        const found = this.findNodeByKey(node.children, key);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  onSelectionChange(selectedTreeNodes: TreeNode[] | null): void {
    const selectedCountries = this.convertTreeNodesToCountries(selectedTreeNodes);

    this.formGroupInput.get(this.formControlNameInput)?.setValue(selectedCountries, { emitEvent: true });
  }

  convertTreeNodesToCountries(nodes: TreeNode[] | null): CountryModel[] {
    if (!nodes || nodes.length === 0) {
      return [];
    }

    const countries: CountryModel[] = [];

    nodes.forEach((node) => {
      if (node.data && node.data.countryID) {
        countries.push(node.data as CountryModel);
      }
    });

    return countries;
  }

  convertCountriesToTreeNodes(countries: CountryModel[]): TreeNode[] {
    if (!countries || countries.length === 0) {
      return [];
    }

    const selectedNodes: TreeNode[] = [];

    countries.forEach((country) => {
      if (!country) return;

      const node = this.findNodeByKey(this.treeNodes, `country-${country.countryID}`);
      if (node) {
        selectedNodes.push(node);
      }
    });

    return selectedNodes;
  }
}
